// @mui
import {alpha, styled} from '@mui/material/styles';
import Box from '@mui/material/Box';

// ----------------------------------------------------------------------

/**
 * Vérifie si une couleur est une couleur hexadécimale
 * @param {string} color - La couleur à vérifier
 * @return {boolean} True si c'est une couleur hex
 */
const isHexColor = (color) => {
  return typeof color === 'string' && /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color);
};

/**
 * Génère une couleur de contraste pour une couleur hexadécimale
 * @param {string} hexColor - Couleur hexadécimale
 * @return {string} Couleur de contraste (noir ou blanc)
 */
const getContrastColor = (hexColor) => {
  // Convertir hex en RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculer la luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Retourner blanc ou noir selon la luminance
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Génère une version plus claire ou plus foncée d'une couleur hex
 * @param {string} hexColor - Couleur hexadécimale
 * @param {number} factor - Facteur de modification (0.5 = plus foncé, 1.5 = plus clair)
 * @return {string} Couleur modifiée
 */
const adjustHexColor = (hexColor, factor) => {
  const r = Math.round(Math.min(255, parseInt(hexColor.slice(1, 3), 16) * factor));
  const g = Math.round(Math.min(255, parseInt(hexColor.slice(3, 5), 16) * factor));
  const b = Math.round(Math.min(255, parseInt(hexColor.slice(5, 7), 16) * factor));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const StyledLabel = styled(Box)(({theme, ownerState}) => {
  const isLight = theme.palette.mode === 'light';

  const filledVariant = ownerState.variant === 'filled';

  const outlinedVariant = ownerState.variant === 'outlined';

  const softVariant = ownerState.variant === 'soft';

  // Vérifier si la couleur est hexadécimale
  const isCustomHexColor = isHexColor(ownerState.color);

  const defaultStyle = {
    ...(ownerState.color === 'default' && {
      // FILLED
      ...(filledVariant && {
        color: isLight ? theme.palette.common.white : theme.palette.grey[800],
        backgroundColor: theme.palette.text.primary,
      }),
      // OUTLINED
      ...(outlinedVariant && {
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
        border: `2px solid ${theme.palette.text.primary}`,
      }),
      // SOFT
      ...(softVariant && {
        color: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.grey[500], 0.16),
      }),
    }),
  };

  // Styles pour les couleurs hexadécimales personnalisées
  const customHexStyle = {
    ...(isCustomHexColor && {
      // FILLED
      ...(filledVariant && {
        color: getContrastColor(ownerState.color),
        backgroundColor: ownerState.color,
      }),
      // OUTLINED
      ...(outlinedVariant && {
        backgroundColor: 'transparent',
        color: ownerState.color,
        border: `2px solid ${ownerState.color}`,
      }),
      // SOFT
      ...(softVariant && {
        color: adjustHexColor(ownerState.color, isLight ? 0.6 : 1.4),
        backgroundColor: alpha(ownerState.color, 0.16),
      }),
    }),
  };

  const colorStyle = {
    ...(ownerState.color !== 'default' && !isCustomHexColor && {
      // FILLED
      ...(filledVariant && {
        color: theme.palette[ownerState.color].contrastText,
        backgroundColor: theme.palette[ownerState.color].main,
      }),
      // OUTLINED
      ...(outlinedVariant && {
        backgroundColor: 'transparent',
        color: theme.palette[ownerState.color].main,
        border: `2px solid ${theme.palette[ownerState.color].main}`,
      }),
      // SOFT
      ...(softVariant && {
        color: theme.palette[ownerState.color][isLight ? 'dark' : 'light'],
        backgroundColor: alpha(theme.palette[ownerState.color].main, 0.16),
      }),
    }),
  };

  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    borderRadius: 6,
    cursor: 'default',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    textTransform: 'capitalize',
    padding: theme.spacing(0, 0.75),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.shorter,
    }),
    ...defaultStyle,
    ...colorStyle,
    ...customHexStyle, // Ajout du support des couleurs hex
  };
});
